import { Alert,Card,Space,Spin,Typography,theme } from "../../../../../../shared/frontend/ui";
import React from "react";
import {
  CONTRIBUTION_STATUS_CANCELLED,
  CONTRIBUTION_STATUS_DUE,
  CONTRIBUTION_STATUS_IN_DEBT_COLLECTION,
  CONTRIBUTION_STATUS_OVERDUE,
  CONTRIBUTION_STATUS_PAID,
  CONTRIBUTION_STATUS_PENDING,
  CONTRIBUTION_STATUS_RESERVED
} from "../../app/constants/contributionCalendarStatus";
import { cardDefaults,spacing,typography } from "../../app/design-system";
import { useI18n } from "../../app/i18n/useI18n";
import { formatDateOnly,formatMoney,formatNumber } from "../../app/units/format";
import type { UnitsSettings } from "../../app/units/units";
import { RestPeriodPreviewChangeList,type RestPeriodPreviewChange } from "./RestPeriodPreviewChangeList";
import { DetailField,DetailList } from "../../../../../../shared/frontend/components/DetailList";
import {
  buildPreviewContributionItems,
  isRestPeriodFeeContribution,
  summarizeRestPeriodImpact,
  type PreviewContributionItem
} from "./restPeriodImpactPreviewItems";
import { ISO_DATE_LENGTH } from "../../app/utils/isoDate";

/** Beträge unterhalb eines halben Cent gelten als 0. */
const MONEY_ROUNDING_TOLERANCE = 0.005;

export type RestPeriodImpactPreviewContribution = {
  id: string;
  contractId?: string | null;
  contractName?: string | null;
  dueDate: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  amount: number;
  paidAmount: number;
  openAmount: number;
  status: number;
  info?: string | null;
  periodDescription?: string | null;
};

export type RestPeriodImpactPreview = {
  contractPackageId: string;
  planName: string;
  restPeriodStartDate?: string | null;
  restPeriodEndDate?: string | null;
  currentEndDate?: string | null;
  previewEndDate?: string | null;
  currentNextDueContribution?: RestPeriodImpactPreviewContribution | null;
  previewNextDueContribution?: RestPeriodImpactPreviewContribution | null;
  currentContributions: RestPeriodImpactPreviewContribution[];
  previewContributions: RestPeriodImpactPreviewContribution[];
  warnings?: string[] | null;
  changes?: RestPeriodPreviewChange[];
};


type RestPeriodImpactPreviewCardProps = {
  preview: RestPeriodImpactPreview | null;
  loading: boolean;
  error: string | null;
  units: UnitsSettings;
  plannedActions?: string[];
};


function formatPeriod(contribution: RestPeriodImpactPreviewContribution, units: UnitsSettings): string {
  if (contribution.periodDescription) return contribution.periodDescription;
  const due = String(contribution.dueDate || "").slice(0, ISO_DATE_LENGTH);
  const start = String(contribution.periodStart || "").slice(0, ISO_DATE_LENGTH);
  const end = String(contribution.periodEnd || "").slice(0, ISO_DATE_LENGTH);
  if (!start || (start === due && (!end || end === start))) return "";
  if (end && end !== start) return `${formatDateOnly(start, units)} - ${formatDateOnly(end, units)}`;
  return formatDateOnly(start, units);
}

function toMemberCashflowAmount(amount: number): number {
  return -Number(amount || 0);
}

function formatSignedCashflowMoney(amount: number, units: UnitsSettings): string {
  const value = toMemberCashflowAmount(amount);
  if (Math.abs(value) < MONEY_ROUNDING_TOLERANCE) return formatMoney(0, units);
  return value > 0 ? `+${formatMoney(value, units)}` : formatMoney(value, units);
}

function isCorrectionContribution(contribution: PreviewContributionItem): boolean {
  return Number(contribution.amount || 0) < 0 || String(contribution.info || "").toLowerCase().includes("korrektur");
}

export function RestPeriodImpactPreviewCard({
  preview,
  loading,
  error,
  units,
  plannedActions = []
}: RestPeriodImpactPreviewCardProps) {
  const { text } = useI18n();
  const { token } = theme.useToken();
  const contributionItems = React.useMemo(() => (preview ? buildPreviewContributionItems(preview) : []), [preview]);
  const summary = React.useMemo(() => summarizeRestPeriodImpact(contributionItems), [contributionItems]);
  const hasEndChange = String(preview?.currentEndDate || "") !== String(preview?.previewEndDate || "");
  const displayedContributionItems = React.useMemo(
    () => contributionItems.filter(item => item.changeKind !== "unchanged"),
    [contributionItems]
  );

  const followUpActions = [...(preview?.changes?.some(change => change.area === 0) ? ["Rest period preview history action"] : []), ...plannedActions];

  const statusLabel = React.useCallback(
    (status: number, amount?: number) => {
      if (Number(amount || 0) < 0) return text("Applied correction");
      if (status === CONTRIBUTION_STATUS_CANCELLED) return text("Open item status cancelled");
      if (status === CONTRIBUTION_STATUS_PENDING) return text("Scheduled");
      if (status === CONTRIBUTION_STATUS_DUE) return text("Due");
      if (status === CONTRIBUTION_STATUS_OVERDUE) return text("Reminder eligible (status)");
      if (status === CONTRIBUTION_STATUS_PAID) return text("Paid (status)");
      if (status === CONTRIBUTION_STATUS_RESERVED) return text("Direct debit submitted");
      if (status === CONTRIBUTION_STATUS_IN_DEBT_COLLECTION) return text("Contribution calendar status debt collection");
      return String(status ?? "");
    },
    [text]
  );

  const changeLabel = React.useCallback(
    (kind: PreviewContributionItem["changeKind"]) => {
      if (kind === "new") return text("Rest period preview row new");
      if (kind === "changed") return text("Rest period preview row changed");
      if (kind === "removed") return text("Rest period preview row removed");
      return text("Rest period preview row unchanged");
    },
    [text]
  );

  const contributionTitle = React.useCallback(
    (item: PreviewContributionItem) => {
      if (isRestPeriodFeeContribution(item)) return text("Rest period monthly fee");
      if (isCorrectionContribution(item)) return text("Contribution correction");
      return String(item.contractName || item.info || preview?.planName || "-");
    },
    [preview?.planName, text]
  );

  if (!loading && !error && !preview) return null;

  return (
    <Space direction="vertical" size={spacing.xs} style={{ width: "100%", marginTop: spacing.md }}>
      <Typography.Text strong>{text("Rest period impact preview title")}</Typography.Text>
      <Card
        size="small"
        styles={{ body: cardDefaults.sectionBodyStyleCompact }}
        style={{
          background: token.colorInfoBg,
          borderColor: token.colorInfoBorder
        }}
      >
        {loading ? (
          <Spin size="small" />
        ) : error ? (
          <Alert type="warning" showIcon message={text(error)} />
        ) : preview ? (
          <Space direction="vertical" size={spacing.sm} style={{ width: "100%" }}>
            <DetailList>
              <DetailField label={text("Rest period suspended contributions")}>
                {summary.suspendedCount > 0
                  ? `${formatNumber(summary.suspendedCount, units)} · ${formatMoney(summary.suspendedAmount, units)}`
                  : "—"}
              </DetailField>
              <DetailField label={text("Rest period costs")}>{formatMoney(summary.feeAmount, units)}</DetailField>
              <DetailField label={text("Contract end after saving")}>
                {preview.previewEndDate ? formatDateOnly(String(preview.previewEndDate).slice(0, ISO_DATE_LENGTH), units) : text("No end date")}
                {hasEndChange && preview.currentEndDate ? (
                  <Typography.Text type="secondary">
                    {" "}
                    ({text("Rest period preview before value").replace("{0}", formatDateOnly(String(preview.currentEndDate).slice(0, ISO_DATE_LENGTH), units))})
                  </Typography.Text>
                ) : null}
              </DetailField>
            </DetailList>
            {preview.warnings && preview.warnings.length > 0 ? (
              <Typography.Text type="warning">
                {preview.warnings.map((warning) => text(warning)).join(" ")}
              </Typography.Text>
            ) : null}
            {displayedContributionItems.length > 0 ? (
              <Space direction="vertical" size={spacing.xs} style={{ width: "100%" }}>
                <Typography.Text type="secondary">{text("Contributions after saving")}</Typography.Text>
                {displayedContributionItems.map((item) => {
                  const amountAfter = item.changeKind === "removed" ? 0 : Number(item.amount || 0);
                  const amountBefore = item.before ? Number(item.before.amount || 0) : null;
                  const period = formatPeriod(item, units);
                  const before = item.before;
                  const details: string[] = [];
                  if (before && item.changeKind === "changed") {
                    if (before.dueDate !== item.dueDate) details.push(`${text("Due date")}: ${formatDateOnly(before.dueDate, units)} → ${formatDateOnly(item.dueDate, units)}`);
                    if (before.status !== item.status) details.push(`${text("Status")}: ${statusLabel(before.status, before.amount)} → ${statusLabel(item.status, item.amount)}`);
                    if (before.paidAmount !== item.paidAmount) details.push(`${text("Paid (status)")}: ${formatMoney(before.paidAmount, units)} → ${formatMoney(item.paidAmount, units)}`);
                    if (before.openAmount !== item.openAmount) details.push(`${text("Open amount")}: ${formatMoney(before.openAmount, units)} → ${formatMoney(item.openAmount, units)}`);
                    const oldPeriod = formatPeriod(before, units);
                    if (oldPeriod !== period) details.push(`${text("Period")}: ${oldPeriod || "—"} → ${period || "—"}`);
                    if (before.contractName !== item.contractName) details.push(`${text("Contract")}: ${before.contractName || "—"} → ${item.contractName || "—"}`);
                    if (before.info !== item.info) details.push(`${text("Description")}: ${before.info || "—"} → ${item.info || "—"}`);
                  }
                  return (
                    <div
                      key={`${item.changeKind}-${item.id}`}
                      style={{
                        padding: `${spacing.xs}px 0`,
                        minWidth: 0
                      }}
                    >
                      <Space direction="vertical" size={spacing.xs} style={{ width: "100%" }}>
                        <Space style={{ justifyContent: "space-between", width: "100%", gap: spacing.sm, alignItems: "flex-start" }}>
                          <Typography.Text strong style={{ overflowWrap: "anywhere" }}>
                            {contributionTitle(item)}
                          </Typography.Text>
                          <Typography.Text strong style={{ whiteSpace: "nowrap", fontVariantNumeric: typography.fontVariantNumeric }}>
                            {formatSignedCashflowMoney(amountAfter, units)}
                          </Typography.Text>
                        </Space>
                        <Typography.Text type="secondary" style={{ fontSize: typography.fontSize.sm, overflowWrap: "anywhere" }}>
                          {text("Due date")}: {formatDateOnly(String(item.dueDate || "").slice(0, ISO_DATE_LENGTH), units)}
                          {amountBefore != null && amountBefore !== amountAfter
                            ? ` · ${text("Rest period preview before value").replace("{0}", formatSignedCashflowMoney(amountBefore, units))}`
                            : ""}
                          {period ? ` · ${text("Period")}: ${period}` : ""}
                          {" · "}
                          {changeLabel(item.changeKind)} · {statusLabel(Number(item.status || 0), item.amount)}
                        </Typography.Text>
                        {details.map(detail => <Typography.Text key={detail} type="secondary" style={{ fontSize: typography.fontSize.sm }}>{detail}</Typography.Text>)}
                      </Space>
                    </div>
                  );
                })}
              </Space>
            ) : null}
            <RestPeriodPreviewChangeList changes={preview.changes ?? []} units={units} />
            {followUpActions.length > 0 ? (
              <Space direction="vertical" size={spacing.xs}>
                <Typography.Text type="secondary">{text("Rest period preview follow-up actions")}</Typography.Text>
                {followUpActions.map(action => <Typography.Text key={action}>{text(action)}</Typography.Text>)}
              </Space>
            ) : null}
          </Space>
        ) : null}
      </Card>
    </Space>
  );
}
